import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { WaterCard } from "@/components/nutrition/WaterCard";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ForkKnife, Camera, PencilSimple, Trash, CaretLeft, CaretRight, GearSix, Fire, Clock, Barbell, ChatCircleDots } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer } from "recharts";

const imgSrc = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/nutrition/photo-file?path=${encodeURIComponent(p)}`;
const iso = (d) => d.toISOString().slice(0, 10);
const fmtDate = (ds) => new Date(ds + "T12:00:00").toLocaleDateString("ru-RU", { weekday: "short", day: "numeric", month: "long" });
const nowTime = () => new Date().toTimeString().slice(0, 5);
const MEAL_TYPES = { breakfast: "Завтрак", lunch: "Обед", dinner: "Ужин", snack: "Перекус" };
const CONF = { high: ["высокая", "#00e676"], medium: ["средняя", "#ffea00"], low: ["низкая", "#ff9100"] };

const MacroBar = ({ label, value, color, max }) => (
  <div className="flex-1 min-w-0">
    <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
      <span>{label}</span><b className="num text-foreground">{value} г</b>
    </div>
    <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${Math.min(100, (value / max) * 100)}%`, background: color }} />
    </div>
  </div>
);

export default function NutritionPage() {
  const [dateS, setDateS] = useState(iso(new Date()));
  const [day, setDay] = useState(null);
  const [week, setWeek] = useState(null);
  const [insights, setInsights] = useState(null);
  const [busy, setBusy] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualText, setManualText] = useState("");
  const [setOpen, setSetOpen] = useState(false);
  const [normKcal, setNormKcal] = useState("");
  const [normProt, setNormProt] = useState("");
  const [normH, setNormH] = useState("");
  const [normW, setNormW] = useState("");
  const [waterKey, setWaterKey] = useState(0);
  const [photoDlg, setPhotoDlg] = useState(null);
  const [hint, setHint] = useState("");
  const [refineFor, setRefineFor] = useState(null);
  const [refineText, setRefineText] = useState("");
  const [editDlg, setEditDlg] = useState(null);
  const fileRef = useRef(null);

  const loadDay = (d = dateS) => api.get(`/nutrition/day?date=${d}`).then((r) => setDay(r.data)).catch(() => {});
  const loadExtras = () => {
    api.get("/nutrition/week").then((r) => setWeek(r.data)).catch(() => {});
    api.get("/nutrition/insights").then((r) => setInsights(r.data)).catch(() => {});
  };
  useEffect(() => { loadDay(dateS); }, [dateS]); // eslint-disable-line
  useEffect(() => { loadExtras(); }, []); // eslint-disable-line

  const shiftDay = (n) => {
    const d = new Date(dateS + "T12:00:00");
    d.setDate(d.getDate() + n);
    setDateS(iso(d));
  };

  const onPhoto = (file) => {
    if (!file) return;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = Math.min(1, 1536 / Math.max(img.width, img.height));
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        setHint("");
        setPhotoDlg({ blob, preview: URL.createObjectURL(blob) });
      }, "image/jpeg", 0.8);
    };
    img.src = URL.createObjectURL(file);
  };

  const submitPhoto = async () => {
    try {
      setBusy(true);
      const fd = new FormData();
      fd.append("file", photoDlg.blob, "food.jpg");
      fd.append("date_s", dateS);
      fd.append("time_s", nowTime());
      fd.append("hint", hint.trim());
      const { data } = await api.post("/nutrition/photo", fd);
      if (!data.meal) toast.error(data.comment || "Еды на фото не видно");
      else { toast.success(data.comment || "Добавлено"); setPhotoDlg(null); loadDay(); loadExtras(); }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const addManual = async () => {
    if (!manualText.trim()) return;
    try {
      setBusy(true);
      const { data } = await api.post("/nutrition/manual", { text: manualText, date: dateS, time: nowTime() });
      if (!data.meal) toast.error(data.comment || "Не удалось распознать");
      else { toast.success(data.comment || "Добавлено"); setManualOpen(false); setManualText(""); loadDay(); loadExtras(); }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const refine = async () => {
    if (!refineText.trim()) return;
    try {
      setBusy(true);
      await api.post(`/nutrition/meals/${refineFor}/refine`, { text: refineText });
      toast.success("Пересчитано с учётом уточнения");
      setRefineFor(null); setRefineText("");
      loadDay(); loadExtras();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const removeMeal = async (id) => {
    await api.delete(`/nutrition/meals/${id}`).catch(() => {});
    loadDay(); loadExtras();
  };

  const openEdit = (meal, dishIdx) => setEditDlg({
    mealId: meal.id, dishes: meal.dishes.map((d) => ({ ...d })), idx: dishIdx,
    meal_type: meal.meal_type, grams: String(meal.dishes[dishIdx].grams || ""),
  });

  const applyGrams = () => {
    const g = Math.max(0, Math.min(5000, Number(editDlg.grams) || 0));
    const d = editDlg.dishes[editDlg.idx];
    const k = d.grams > 0 ? g / d.grams : 1;
    const nd = { ...d, grams: g, kcal: Math.round(d.kcal * k), protein: Math.round(d.protein * k), fat: Math.round(d.fat * k), carb: Math.round(d.carb * k) };
    return editDlg.dishes.map((x, i) => (i === editDlg.idx ? nd : x));
  };

  const saveEdit = async (dishes) => {
    try {
      await api.put(`/nutrition/meals/${editDlg.mealId}`, { dishes, meal_type: editDlg.meal_type });
      toast.success("Обновлено");
      setEditDlg(null);
      loadDay(); loadExtras();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveNorms = async () => {
    try {
      await api.put("/nutrition/settings", {
        kcal_norm: Number(normKcal), protein_norm: Number(normProt),
        height_cm: normH ? Number(normH) : null, weight_kg: normW ? Number(normW) : null,
      });
      toast.success("Нормы обновлены");
      setSetOpen(false); loadDay(); loadExtras(); setWaterKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const pct = day ? Math.min(100, Math.round((day.totals.kcal / day.norm.kcal) * 100)) : 0;
  const ratio = day && day.norm.kcal ? day.totals.kcal / day.norm.kcal : 0;
  const status = !day || day.totals.kcal === 0 ? "empty" : ratio > 1.05 ? "over" : ratio >= 0.95 ? "ok" : "under";
  const ringColor = status === "over" ? "#ff3b30" : status === "ok" ? "#00e676" : "#ff9100";
  const openNorms = async () => {
    setNormKcal(String(day?.norm.kcal || 2200)); setNormProt(String(day?.norm.protein || 100));
    try {
      const { data: w } = await api.get(`/nutrition/water?date=${dateS}`);
      setNormH(w.height_cm ? String(w.height_cm) : "");
      setNormW(w.weight_kg ? String(w.weight_kg) : "");
    } catch {}
    setSetOpen(true);
  };

  return (
    <div className="fade-in" data-testid="nutrition-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1.5"><ForkKnife size={14} weight="fill" className="text-[#ff9100]" />Дневник питания</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">Питание</h1>
        </div>
        <button onClick={openNorms}
          data-testid="nutrition-settings-btn" className="p-2 rounded-lg glass-panel border border-white/[0.07] text-muted-foreground hover:text-foreground">
          <GearSix size={18} />
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => shiftDay(-1)} data-testid="nutrition-prev-day" className="p-1.5 rounded-md glass-panel border border-white/[0.07]"><CaretLeft size={16} /></button>
        <span className="text-sm font-semibold min-w-[180px] text-center" data-testid="nutrition-date">{fmtDate(dateS)}</span>
        <button onClick={() => shiftDay(1)} disabled={dateS >= iso(new Date())} data-testid="nutrition-next-day"
          className="p-1.5 rounded-md glass-panel border border-white/[0.07] disabled:opacity-30"><CaretRight size={16} /></button>
      </div>

      {day && (
        <div className="glass-card p-5 mb-4" data-testid="nutrition-summary">
          <div className="flex items-center gap-5 flex-wrap">
            <div className="relative w-24 h-24 shrink-0">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="9" />
                <circle cx="50" cy="50" r="42" fill="none" stroke={ringColor} strokeWidth="9"
                  strokeDasharray={`${(pct / 100) * 264} 264`} strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-heading font-bold num text-lg leading-none">{day.totals.kcal}</span>
                <span className="text-[9px] text-muted-foreground">из {day.norm.kcal}</span>
              </div>
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                {status === "empty" && (
                  <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-white/[0.06] text-muted-foreground" data-testid="kcal-status">
                    Пока пусто · норма {day.norm.kcal} ккал
                  </span>
                )}
                {status === "under" && (
                  <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-[#4dabf5]/15 text-[#4dabf5] ring-1 ring-[#4dabf5]/25" data-testid="kcal-status">
                    ↓ Не добрал · осталось {day.norm.kcal - day.totals.kcal} ккал
                  </span>
                )}
                {status === "ok" && (
                  <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/25" data-testid="kcal-status">
                    ✓ Норма добрана{day.totals.kcal > day.norm.kcal ? ` (+${day.totals.kcal - day.norm.kcal})` : ""}
                  </span>
                )}
                {status === "over" && (
                  <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-500/15 text-red-400 ring-1 ring-red-500/30" data-testid="kcal-status">
                    ↑ Перебор на {day.totals.kcal - day.norm.kcal} ккал
                  </span>
                )}
                <button onClick={openNorms} data-testid="kcal-norm-edit-btn"
                  className="text-[11px] font-semibold text-muted-foreground hover:text-foreground underline decoration-dotted underline-offset-2 transition-colors">
                  норма {day.norm.kcal} ккал · изменить
                </button>
              </div>
              <div className="flex gap-4">
                <MacroBar label="Белки" value={day.totals.protein} color="#00e676" max={day.norm.protein} />
                <MacroBar label="Жиры" value={day.totals.fat} color="#ffea00" max={Math.round(day.norm.kcal * 0.3 / 9)} />
                <MacroBar label="Углеводы" value={day.totals.carb} color="#4dabf5" max={Math.round(day.norm.kcal * 0.5 / 4)} />
              </div>
            </div>
          </div>
        </div>
      )}

      <WaterCard date={dateS} refreshKey={waterKey} onEditNorm={openNorms} />

      <div className="flex gap-2 mb-5">
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="nutrition-photo-btn"
          className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#ff9100] text-black text-sm font-bold disabled:opacity-50 hover:brightness-110 transition-all">
          <Camera size={18} weight="fill" /> Фото еды
        </button>
        <button onClick={() => setManualOpen(true)} disabled={busy} data-testid="nutrition-manual-btn"
          className="flex items-center gap-2 px-4 py-3 rounded-xl glass-panel border border-white/[0.1] text-sm font-semibold disabled:opacity-50">
          <PencilSimple size={17} /> Текстом
        </button>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
          data-testid="nutrition-file-input" onChange={(e) => { onPhoto(e.target.files?.[0]); e.target.value = ""; }} />
      </div>

      <div className="space-y-3 mb-6" data-testid="meals-list">
        {day?.meals.map((m) => (
          <div key={m.id} className="glass-card p-4 flex gap-3" data-testid={`meal-${m.id}`}>
            {m.photo && <img src={imgSrc(m.photo)} alt="" className="w-20 h-20 rounded-lg object-cover shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                {m.meal_type && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#ff9100]/15 text-[#ff9100] font-semibold" data-testid={`meal-type-${m.id}`}>{MEAL_TYPES[m.meal_type]}</span>}
                <span className="text-[10px] text-muted-foreground num">{m.time}</span>
                {m.confidence && (
                  <span className="flex items-center gap-1 text-[10px] text-muted-foreground" data-testid={`meal-conf-${m.id}`}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: CONF[m.confidence]?.[1] }} />
                    точность {CONF[m.confidence]?.[0]}
                  </span>
                )}
                <span className="ml-auto font-heading font-bold num text-[#ff9100]">{m.totals.kcal} ккал</span>
                <button onClick={() => removeMeal(m.id)} data-testid={`meal-delete-${m.id}`}
                  className="text-muted-foreground hover:text-red-400 transition-colors"><Trash size={15} /></button>
              </div>
              <div className="flex flex-wrap gap-1 mb-1">
                {m.dishes.map((d, i) => (
                  <button key={i} onClick={() => openEdit(m, i)} data-testid={`dish-chip-${m.id}-${i}`}
                    className="text-[11px] px-2 py-0.5 rounded-full bg-white/[0.05] border border-white/[0.08] hover:border-[#ff9100]/50 transition-colors">
                    {d.name} <span className="num text-muted-foreground">{d.grams > 0 && `${d.grams}г · `}{d.kcal}</span>
                  </button>
                ))}
              </div>
              <div className="text-[10px] text-muted-foreground num">Б {m.totals.protein} · Ж {m.totals.fat} · У {m.totals.carb}</div>
              {m.assumptions && <p className="text-[10px] text-muted-foreground mt-1 italic">Допущения: {m.assumptions}</p>}
              {m.comment && <p className="text-[11px] text-muted-foreground mt-1 italic">{m.comment}</p>}
              <button onClick={() => { setRefineFor(m.id); setRefineText(""); }} data-testid={`meal-refine-${m.id}`}
                className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-[#4dabf5] hover:brightness-125">
                <ChatCircleDots size={13} /> Уточнить
              </button>
            </div>
          </div>
        ))}
        {day && day.meals.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8" data-testid="meals-empty">Сфотографируйте первый приём пищи — AI распознает блюда и посчитает КБЖУ</p>
        )}
      </div>

      {week && week.days.some((d) => d.kcal > 0) && (
        <div className="glass-card p-5 mb-4" data-testid="nutrition-week">
          <h3 className="text-sm font-semibold mb-3">Последние 7 дней</h3>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={week.days} margin={{ top: 5, right: 5, bottom: 0, left: -25 }}>
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(8)} tick={{ fontSize: 10, fill: "#8b93a7" }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#8b93a7" }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "rgba(15,18,28,0.95)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                formatter={(v) => [`${v} ккал`]} labelFormatter={(d) => fmtDate(d)} />
              <ReferenceLine y={week.norm.kcal} stroke="#ff9100" strokeDasharray="4 4" />
              <Bar dataKey="kcal" fill="#ff9100" radius={[4, 4, 0, 0]} fillOpacity={0.75} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {insights && (
        <div className="glass-card p-5" data-testid="nutrition-insights">
          <h3 className="text-sm font-semibold mb-3">Питание × Восстановление</h3>
          {insights.insights.length > 0 ? (
            <div className="space-y-2">
              {insights.insights.map((ins, i) => (
                <div key={i} className="flex items-start gap-2.5 rounded-lg bg-white/[0.04] border border-white/[0.07] px-3 py-2.5 text-xs">
                  {ins.icon === "clock" ? <Clock size={16} className="text-[#a970ff] shrink-0 mt-0.5" />
                    : ins.icon === "protein" ? <Barbell size={16} className="text-[#00e676] shrink-0 mt-0.5" />
                    : <Fire size={16} className="text-[#ff9100] shrink-0 mt-0.5" />}
                  <span>{ins.text}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">{insights.note}</p>
          )}
        </div>
      )}

      <Dialog open={!!photoDlg} onOpenChange={(v) => !v && !busy && setPhotoDlg(null)}>
        <DialogContent className="glass-modal">
          <DialogHeader><DialogTitle>Распознать еду</DialogTitle></DialogHeader>
          {photoDlg && <img src={photoDlg.preview} alt="" className="w-full max-h-56 object-cover rounded-lg" />}
          <Input value={hint} onChange={(e) => setHint(e.target.value)} data-testid="photo-hint-input"
            placeholder="Подсказка AI (необязательно): «200 г творога», «жарено на масле»" />
          <Button onClick={submitPhoto} disabled={busy} data-testid="photo-submit">
            {busy ? "AI анализирует..." : "Распознать"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="glass-modal">
          <DialogHeader><DialogTitle>Что вы съели?</DialogTitle></DialogHeader>
          <Input value={manualText} onChange={(e) => setManualText(e.target.value)} data-testid="manual-food-input"
            placeholder="Например: 2 яйца, тост с авокадо и капучино" onKeyDown={(e) => e.key === "Enter" && addManual()} />
          <Button onClick={addManual} disabled={busy || !manualText.trim()} data-testid="manual-food-submit">
            {busy ? "AI считает КБЖУ..." : "Добавить"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!refineFor} onOpenChange={(v) => !v && !busy && setRefineFor(null)}>
        <DialogContent className="glass-modal">
          <DialogHeader><DialogTitle>Уточнить для AI</DialogTitle></DialogHeader>
          <Input value={refineText} onChange={(e) => setRefineText(e.target.value)} data-testid="refine-input"
            placeholder="«Курицы было 150 г, ещё была сметана»" onKeyDown={(e) => e.key === "Enter" && refine()} />
          <Button onClick={refine} disabled={busy || !refineText.trim()} data-testid="refine-submit">
            {busy ? "AI пересчитывает..." : "Пересчитать"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editDlg} onOpenChange={(v) => !v && setEditDlg(null)}>
        <DialogContent className="glass-modal">
          <DialogHeader><DialogTitle>{editDlg && editDlg.dishes[editDlg.idx]?.name}</DialogTitle></DialogHeader>
          {editDlg && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground">Вес, г (КБЖУ пересчитаются пропорционально)</label>
                <Input type="number" value={editDlg.grams} onChange={(e) => setEditDlg({ ...editDlg, grams: e.target.value })} data-testid="dish-grams-input" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Тип приёма</label>
                <div className="flex gap-1 mt-1">
                  {Object.entries(MEAL_TYPES).map(([id, label]) => (
                    <button key={id} onClick={() => setEditDlg({ ...editDlg, meal_type: id })} data-testid={`meal-type-btn-${id}`}
                      className={`flex-1 py-1.5 rounded-md text-[11px] font-semibold transition-colors ${editDlg.meal_type === id ? "bg-[#ff9100] text-black" : "bg-white/[0.05] text-muted-foreground"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => saveEdit(applyGrams())} data-testid="dish-save-btn" className="flex-1">Сохранить</Button>
                {editDlg.dishes.length > 1 && (
                  <Button variant="destructive" data-testid="dish-delete-btn"
                    onClick={() => saveEdit(editDlg.dishes.filter((_, i) => i !== editDlg.idx))}>
                    Удалить блюдо
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={setOpen} onOpenChange={setSetOpen}>
        <DialogContent className="glass-modal">
          <DialogHeader><DialogTitle>Дневные нормы</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Калории, ккал</label>
              <Input type="number" value={normKcal} onChange={(e) => setNormKcal(e.target.value)} data-testid="norm-kcal-input" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Белок, г</label>
              <Input type="number" value={normProt} onChange={(e) => setNormProt(e.target.value)} data-testid="norm-protein-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Рост, см</label>
                <Input type="number" value={normH} onChange={(e) => setNormH(e.target.value)} data-testid="norm-height-input" placeholder="175" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Вес, кг</label>
                <Input type="number" value={normW} onChange={(e) => setNormW(e.target.value)} data-testid="norm-weight-input" placeholder="70" />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-1">Рост и вес задают норму воды (35 мл × кг) и учитываются AI-ассистентом здоровья</p>
            <Button onClick={saveNorms} data-testid="norm-save-btn">Сохранить</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
